/**
 * Maps a user's `userType` (the onboarding role) to the dashboard route they
 * should land on. Used in two places that must agree to avoid a redirect race:
 *  - App.tsx: the /complete-profile route guard, so that once a userType is set
 *    (e.g. by the auto-apply on registration) the guard routes to the role's
 *    dashboard rather than a generic /feed.
 *  - CompleteProfilePage: after applying a userType, navigate to the same place.
 *  - LoginPage: post-login redirect.
 *
 * The six TB-supporter categories each land on the adaptive
 * /supporter-dashboard/<slug> route, which renders one shared component that
 * adapts hero + sections per type.
 */
export type UserType =
  | 'USER'
  | 'DOCTOR'
  | 'HEALTH_WORKER'
  | 'NGO'
  | 'NIKSHAY_MITRA'
  | 'SUPPORTER_INDIVIDUAL'
  | 'SUPPORTER_NGO'
  | 'SUPPORTER_CORPORATE'
  | 'NUTRITION_SPONSOR'
  | 'CSR_PARTNER'
  | 'VOLUNTEER_COMMUNITY'

/** The supporter-category slugs that the adaptive dashboard accepts.
 *  'csr' was removed when the CSR-partner persona was retired. */
export const SUPPORTER_SLUGS = [
  'individual',
  'ngo',
  'corporate',
  'nutrition',
  'volunteer',
] as const
export type SupporterSlug = (typeof SUPPORTER_SLUGS)[number]

/** userType → supporter-dashboard slug (undefined if not a supporter type).
 *  NOTE: VOLUNTEER_COMMUNITY is NOT a supporter slug — it has its own dedicated
 *  /community-dashboard shell (see destinationForUserType). The 'volunteer' slug
 *  remains in SUPPORTER_SLUGS only for back-compat and is redirected from
 *  SupporterDashboard.tsx → /community-dashboard.
 *  CSR_PARTNER no longer maps to a slug; such users fall through to the generic
 *  /feed landing (see destinationForUserType). */
export function supporterSlugForUserType(
  userType: string | undefined | null,
): SupporterSlug | undefined {
  switch (userType) {
    case 'SUPPORTER_INDIVIDUAL':
      return 'individual'
    case 'SUPPORTER_NGO':
      return 'ngo'
    case 'SUPPORTER_CORPORATE':
      return 'corporate'
    case 'NUTRITION_SPONSOR':
      return 'nutrition'
    default:
      return undefined
  }
}

export function destinationForUserType(userType: string | undefined | null): string {
  // Volunteer Community users land on the regular /feed shell (the main app),
  // NOT the dedicated /community-dashboard shell. The volunteer-unique features
  // (Opportunities, My Activities, Learning, Community, Resources) are reached
  // from the left sidebar's "Volunteering" section (links into /community-dashboard/*).
  if (userType === 'VOLUNTEER_COMMUNITY') return '/feed'
  const slug = supporterSlugForUserType(userType)
  if (slug) return `/supporter-dashboard/${slug}`
  switch (userType) {
    case 'USER':
      return '/dashboard'
    case 'NGO':
    case 'NIKSHAY_MITRA':
    default:
      // Retired personas (DOCTOR, HEALTH_WORKER, CSR_PARTNER) and any unknown
      // type land on the generic feed rather than a removed dashboard route.
      return '/feed'
  }
}